"use client";

import { useSession } from "@/lib/auth/use-session";
import { useAuthHint } from "@/lib/auth/auth-hint";

/**
 * Renders its children ONLY for logged-out visitors.
 *
 * Public pages (/pricing, /terms, /privacy, /refund) ship their own marketing
 * header/nav for logged-out visitors. But ConditionalAppShell wraps those same
 * pages in the authed AppShell for logged-in users — which already provides the
 * sidebar, user button and a way back into the app. Without this guard a
 * logged-in user saw BOTH chromes (double nav) plus a "Sign in / Start Free"
 * button they don't need. Wrap the page's own header in <HideWhenAuthed> so it
 * only shows when there's no session.
 *
 * Mounted-gated: SSR has no session and renders the children; the first client
 * render also renders them (mounted=false) so hydration agrees, then it hides
 * them one tick later for authed users. (Same pattern as the app-shell
 * useMounted() gate — avoids React #418.)
 */
export function HideWhenAuthed({ children }: { children: React.ReactNode }) {
	const { user, status } = useSession();
	const hint = useAuthHint();
	// The server's cookie hint is the same on the server and in the first client render, so there is no swap after load.
	const signedIn = status === "loading" ? hint : Boolean(user);
	return signedIn ? null : <>{children}</>;
}
